import formatBasisPoints from 'src/public/formatBasisPoints/formatBasisPoints';
import formatCurrency from 'src/public/formatCurrency/formatCurrency';
import formatDateTime from 'src/public/formatDateTime/formatDateTime';
import formatDisplayName from 'src/public/formatDisplayName/formatDisplayName';
import formatList from 'src/public/formatList/formatList';
import formatMessage from 'src/public/formatMessage/formatMessage';
import formatNumber from 'src/public/formatNumber/formatNumber';
import formatPercent from 'src/public/formatPercent/formatPercent';
import formatRelativeTime from 'src/public/formatRelativeTime/formatRelativeTime';
import getTimezoneLabels from 'src/public/getTimezoneLabels/getTimezoneLabels';
import isTranslated from 'src/public/isTranslated/isTranslated';

import type {IAnalytics, IReports} from '@sail/observability';
import type {FormatListDeclarativeOverloaded} from 'src/public/formatList/types';
import type {FormatMessageDeclarative} from 'src/public/formatMessage/formatMessage';
import type {IntlHook} from 'src/public/useIntl/types';
import type {IntlConfig} from 'src/types';

function noSetLocale(): Promise<void> {
  return Promise.reject(
    new Error(
      '[@sail/intl] setLocale was not configured. Provide a setLocale function in IntlProvider config.',
    ),
  );
}

/**
 * Creates the object returned by `useIntl` for server-side and other non-React
 * code. Callers must provide locale data through `config` and supply reporting
 * and analytics dependencies. React components should prefer `useIntl`, which
 * obtains these dependencies from providers automatically.
 *
 * @see https://trailhead.corp.stripe.com/docs/sail-intl/api/useIntl#create-intl-object
 */
export default function createIntlObject(
  config: IntlConfig,
  reports: IReports,
  analytics: IAnalytics,
): IntlHook {
  return {
    formatBasisPoints: formatBasisPoints.bind(null, config, reports, analytics),
    formatCurrency: formatCurrency.bind(null, config, reports),
    formatDateTime: formatDateTime.bind(null, config, reports, analytics),
    formatDisplayName: formatDisplayName.bind(null, config, reports),
    formatList: formatList.bind(
      null,
      config,
      reports,
    ) as FormatListDeclarativeOverloaded,
    formatMessage: formatMessage.bind(
      null,
      config,
      reports,
      analytics,
    ) as FormatMessageDeclarative,
    formatNumber: formatNumber.bind(null, config, reports, analytics),
    formatPercent: formatPercent.bind(null, config, reports, analytics),
    formatRelativeTime: formatRelativeTime.bind(
      null,
      config,
      reports,
      analytics,
    ),
    getTimezoneLabels: getTimezoneLabels.bind(null, config, reports),
    isTranslated: isTranslated.bind(null, config, reports, analytics),
    autoDetect: config.autoDetect,
    locale: config.locale,
    setLocale: config.setLocale || noSetLocale,
  };
}
